/* eslint-disable react/jsx-no-bind */

import PropTypes from 'prop-types';
import React, { Component } from 'react';
import TreeView from 'portfolio-shared/view/Utilities/TreeView';
import logger from 'portfolio-shared/util/logging';
import ReactDOM from 'react-dom';
import {
  FILETYPE,
  getNextPath,
  getFolderForPath,
} from 'portfolio-shared/util/folderUtils';
import { PRODUCT_ADMIN } from 'portfolio-shared/util/sharedConstants';

const { array, func, number, object, string } = PropTypes;

// Admin doesn't have CSS modules so mock it using global CSS names
const browseStyles =
  process.env.REACT_APP_PRODUCT === PRODUCT_ADMIN
    ? {
        arrow: 'arrow',
        folderIconContainer: 'folderIconContainer',
        folderList: 'folderList',
        folderListActive: 'folderListActive',
        folderListContainer: 'folderListContainer',
        folderListInner: 'folderListInner',
        noselect: 'noselect',
      }
    : require('./BrowseServerFolders.scss');

const TREE_ITEM_HEIGHT = 23;
const OUTER_DIV_ID = 'serverFoldersContainer';

export class ServerFolders extends Component {
  static propTypes = {
    fileFilter: func,
    treeArray: array,
    onToggleCollapse: func.isRequired,
    onFolderSelected: func.isRequired,
    selectedFolderPath: string,
    outerDivStyle: string.isRequired,
    pathSeparator: string.isRequired,
  };

  static defaultProps = {
    fileFilter: tree => true,
  };

  constructor() {
    super();
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  // This is intended to be used by the containing and must be retrieved via a ref.
  handleKeyDown(e) {
    let result = false;
    if (e.keyCode === 38 || e.keyCode === 40) {
      result = true;
      // The up and down arrow keys
      e.preventDefault();
      // Get the next or previous path depending on the key.
      const nextPath = getNextPath(
        this.props.treeArray,
        this.props.selectedFolderPath,
        e.keyCode === 38
      );
      if (nextPath) {
        this.props.onFolderSelected(nextPath);
      }
    } else if (e.keyCode === 37 || e.keyCode === 39) {
      result = true;
      // The left and right arrow keys
      // Don't preventDefault in this case so that these can be captured by other controls.
      const selectedPath = this.props.selectedFolderPath;
      const selectedFolder = getFolderForPath(
        this.props.treeArray,
        selectedPath
      );
      if (selectedFolder) {
        if (e.keyCode === 39) {
          // Expand the folder if it is not already.
          if (!selectedFolder.isExpanded) {
            this.props.onToggleCollapse(selectedPath);
          }
        } else {
          // Close the folder if it is expanded.
          if (selectedFolder.isExpanded) {
            this.props.onToggleCollapse(selectedPath);
          }
        }
      }
    }
    return result;
  }

  render() {
    const { fileFilter, selectedFolderPath, treeArray } = this.props;
    let folders = [];
    if (treeArray && Array.isArray(treeArray) && treeArray.length > 0) {
      folders = treeArray;
    }
    return (
      <div
        tabIndex="0"
        id={OUTER_DIV_ID}
        className={`${this.props.outerDivStyle} ${
          browseStyles.folderListContainer
        }`}
      >
        {folders.filter(fileFilter).map(folder => (
          <ServerFolderSubTree
            fileFilter={fileFilter}
            key={folder.fullPath}
            selectedFolderPath={selectedFolderPath}
            onToggleCollapse={this.props.onToggleCollapse}
            onFolderSelected={this.props.onFolderSelected}
            tree={folder}
            level={0}
          />
        ))}
      </div>
    );
  }
}

export class ServerFolderSubTree extends Component {
  static propTypes = {
    fileFilter: func.isRequired,
    tree: object.isRequired,
    level: number.isRequired,
    onToggleCollapse: func.isRequired,
    onFolderSelected: func.isRequired,
    selectedFolderPath: string,
  };

  constructor() {
    super();
    this.onLabelClick = this.onLabelClick.bind(this);
    this.handleCollapse = this.handleCollapse.bind(this);
    this.isSelected = this.isSelected.bind(this);
  }

  onLabelClick(tree, e) {
    e.preventDefault();
    e.stopPropagation();
    this.props.onFolderSelected(tree.fullPath, e);
  }

  handleCollapse() {
    this.props.onToggleCollapse(this.props.tree.fullPath);
  }

  isSelected() {
    return this.props.selectedFolderPath === this.props.tree.fullPath;
  }

  componentDidUpdate() {
    if (this.isSelected()) {
      // Adjust the scroll bar so that the selected item is visible.
      const me = ReactDOM.findDOMNode(this);
      const myRect = me.getBoundingClientRect();
      const scrollBox = document.getElementById(OUTER_DIV_ID);
      const scrollRect = scrollBox.getBoundingClientRect();
      if (myRect.top >= scrollRect.bottom - TREE_ITEM_HEIGHT / 2) {
        scrollBox.scrollTop = scrollBox.scrollTop + TREE_ITEM_HEIGHT;
      } else if (myRect.bottom <= scrollRect.top + TREE_ITEM_HEIGHT / 2) {
        scrollBox.scrollTop = scrollBox.scrollTop - TREE_ITEM_HEIGHT;
      }
    }
  }

  renderIcon(fsObj) {
    switch (fsObj.type) {
      case FILETYPE.directory:
        return fsObj.isExpanded ? (
          <i className="fa fa-folder-open-o" />
        ) : (
          <i className="fa fa-folder-o" />
        );
      case FILETYPE.file:
        if (fsObj.displayName.toLowerCase().endsWith('.zip')) {
          return <i className="fa fa-file-archive-o" />;
        }
      // falls through
      default:
        return <i className="fa fa-file-o" />;
    }
  }

  render() {
    const { fileFilter, tree, level } = this.props;
    if (!tree) {
      logger.error('BrowseServerFolders render: tree is null');
      return <div />;
    }

    let folderStyle = browseStyles.folderList;
    if (this.isSelected()) {
      folderStyle = browseStyles.folderListActive;
    }
    const label = (
      <div
        data-test-name={tree.fullPath}
        className={browseStyles.folderListInner}
      >
        <div className={browseStyles.folderIconContainer}>
          {this.renderIcon(tree)}
        </div>
        <div
          className={browseStyles.noselect}
          data-test-name="folder-selectable"
        >
          {tree.displayName}
        </div>
      </div>
    );

    const plusMinus = !tree.isExpanded ? 'plus' : 'minus';
    const arrow =
      tree.type === FILETYPE.file ? (
        <div className={browseStyles.arrow} style={{ width: '29px' }} />
      ) : (
        <div
          className={browseStyles.arrow}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            this.handleCollapse(tree.fullPath);
          }}
        >
          <i className={`fa fa-${plusMinus}`} />
        </div>
      );

    const indent = <div style={{ minWidth: level * 20 }} />;

    return (
      <TreeView
        labelWrapper={children => <div>{children}</div>}
        nodeLabel={label}
        arrow={arrow}
        indent={indent}
        collapsed={!tree.isExpanded}
        itemClassName={folderStyle}
        onClick={e => this.onLabelClick(tree, e)}
      >
        {tree.s &&
          tree.s
            .filter(fileFilter)
            .map(childTree => (
              <ServerFolderSubTree
                fileFilter={fileFilter}
                key={childTree.fullPath}
                selectedFolderPath={this.props.selectedFolderPath}
                onToggleCollapse={this.props.onToggleCollapse}
                onFolderSelected={this.props.onFolderSelected}
                tree={childTree}
                level={level + 1}
              />
            ))}
      </TreeView>
    );
  }
}



// WEBPACK FOOTER //
// ./src/portfolio-shared/view/Utilities/BrowseServerFolders.js